import format from 'date-fns/format';
import * as yup from 'yup';
import type { TechnicianOptionResponse } from '@t/checkout.types';
import { getDropoffRestrictedDates, getLeadTimeDays } from './installationStepUtils';

const buildSchema = (options: TechnicianOptionResponse[] | null) =>
  yup.object({
    installationMethod: yup.mixed<'TECHNICIAN' | 'SELF'>().oneOf(['TECHNICIAN', 'SELF']).required(),
    hardwareDelivery: yup
      .mixed<'PICKUP' | 'DROPOFF'>()
      .oneOf(['PICKUP', 'DROPOFF'])
      .when('installationMethod', {
        is: 'SELF',
        then: (s) => s.required(),
        otherwise: (s) => s.optional(),
      }),
    deliveryInstructions: yup.string().default(''),
    specialInstructions: yup.string().default(''),
    selectedSlotId: yup.string().nullable(),
    selectedDate: yup
      .date()
      .nullable()
      .when('installationMethod', {
        is: 'SELF',
        then: (s) =>
          s
            .required('A date is required.')
            .test('not-before-lead-time', 'Date is earlier than the available lead time.', function (value) {
              if (!value || !options) return true;
              const method = this.parent.installationMethod;
              const delivery = this.parent.hardwareDelivery;
              const leadDays = getLeadTimeDays(options, method, delivery);
              const min = new Date();
              min.setHours(0, 0, 0, 0);
              min.setDate(min.getDate() + leadDays);
              return value.getTime() >= min.getTime();
            })
            .test('not-day-restricted', 'This date is no longer available, please pick another.', function (value) {
              if (!value || !options) return true;
              if (this.parent.hardwareDelivery !== 'DROPOFF') return true;
              const restricted = getDropoffRestrictedDates(options);
              return !restricted.includes(format(value, 'yyyy-MM-dd'));
            }),
      }),
    selectedTimeSlot: yup
      .object({
        startTime: yup.string().required(),
        endTime: yup.string().required(),
      })
      .nullable()
      .when('installationMethod', {
        is: 'TECHNICIAN',
        then: (s) => s.required('Please select a date and time for your installation appointment.'),
      }),
  });

export const installationStepSchema = buildSchema(null);

export const makeInstallationStepSchema = (options: TechnicianOptionResponse[]) => buildSchema(options);
